import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Initials } from "@/lib/avatar";
import { useT } from "@/i18n";
import { UserCircle, CircleNotch, FloppyDisk, CheckCircle, Warning, ShieldCheck } from "@phosphor-icons/react";

const DATE_ROWS = [
  { key: "birthday", tkey: "prof_birthday" },
  { key: "spouse_birthday", tkey: "prof_spouse_birthday" },
  { key: "anniversary", tkey: "prof_anniversary" },
];

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
    <input
      {...props}
      className="mt-1.5 w-full px-3 py-2.5 text-sm bg-surface border border-black/10 dark:border-white/10 focus:ring-2 focus:ring-primary focus:outline-none"
    />
  </label>
);

export default function MyProfile() {
  const { t } = useT();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [complete, setComplete] = useState(false);
  const [linkedError, setLinkedError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/profile");
        const p = r.data.profile || {};
        setForm({
          call_name: r.data.call_name || "", mobile: r.data.mobile || "",
          spouse_name: r.data.spouse_name || "", classification: r.data.classification || "",
          address: r.data.address || "", full_name: r.data.full_name, ...p,
        });
        setComplete(r.data.profile_complete);
      } catch (e) {
        if (e?.response?.status === 403) setLinkedError(true);
      }
      setLoading(false);
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => (v === "" || v === null || v === undefined ? "" : Number(v));

  const save = async () => {
    setSaving(true);
    const payload = {
      call_name: form.call_name, mobile: form.mobile, spouse_name: form.spouse_name,
      classification: form.classification, address: form.address,
    };
    ["birthday", "spouse_birthday", "anniversary"].forEach((k) => {
      const d = form[`${k}_day`], m = form[`${k}_month`];
      if (d) payload[`${k}_day`] = Number(d);
      if (m) payload[`${k}_month`] = Number(m);
    });
    try {
      const r = await api.put("/me/profile", payload);
      setComplete(r.data.profile_complete);
      toast.success(t("prof_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save (check your dates)");
    }
    setSaving(false);
  };

  if (loading)
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <CircleNotch size={18} className="animate-spin text-primary" weight="bold" /> {t("loading")}
      </div>
    );

  if (linkedError)
    return (
      <div data-testid="profile-not-linked" className="max-w-md border border-accent/40 bg-accent/10 p-8">
        <Warning size={28} weight="duotone" className="text-accent" />
        <h2 className="mt-4 font-display font-extrabold tracking-tighter text-2xl">{t("prof_no_member_title")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("prof_no_member_sub")}</p>
      </div>
    );

  return (
    <div className="max-w-2xl" data-testid="my-profile-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
        <UserCircle size={14} weight="fill" className="text-primary" /> {t("prof_directory_details")}
      </div>
      <div className="flex items-center gap-4">
        <Initials name={form.full_name} size={56} />
        <div>
          <h1 className="font-display font-extrabold tracking-tighter text-3xl sm:text-4xl leading-none">{form.full_name}</h1>
          <div className={`mt-2 inline-flex items-center gap-1.5 text-xs px-2 py-1 border ${complete ? "border-emerald-500/40 text-emerald-600" : "border-amber-500/50 text-amber-700 bg-amber-50"}`}>
            <CheckCircle size={13} weight="fill" /> {complete ? t("prof_complete") : t("prof_incomplete")}
          </div>
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-5" data-testid="profile-form">
        <Field label={t("prof_call_name")} data-testid="input-call-name" value={form.call_name} onChange={(e) => set("call_name", e.target.value)} />
        <Field label={t("prof_mobile")} data-testid="input-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} />
        <Field label={t("prof_classification")} data-testid="input-classification" value={form.classification} onChange={(e) => set("classification", e.target.value)} />
        <Field label={t("prof_spouse_name")} data-testid="input-spouse" value={form.spouse_name} onChange={(e) => set("spouse_name", e.target.value)} />
        <label className="block sm:col-span-2">
          <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{t("prof_address")}</span>
          <textarea
            data-testid="input-address" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)}
            className="mt-1.5 w-full px-3 py-2.5 text-sm bg-surface border border-black/10 dark:border-white/10 focus:ring-2 focus:ring-primary focus:outline-none"
          />
        </label>
      </div>

      <div className="mt-8 border border-black/10 dark:border-white/10 bg-surface">
        <div className="px-5 py-3 border-b border-black/10 dark:border-white/10">
          <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t("prof_occasions")}</span>
        </div>
        <div className="divide-y divide-black/5 dark:divide-white/5">
          {DATE_ROWS.map(({ key, tkey }) => (
            <div key={key} className="flex items-center justify-between gap-4 px-5 py-3">
              <span className="text-sm">{t(tkey)}</span>
              <div className="flex items-center gap-2">
                <input
                  data-testid={`input-${key}-day`} type="number" min="1" max="31" placeholder="DD"
                  value={num(form[`${key}_day`])} onChange={(e) => set(`${key}_day`, e.target.value)}
                  className="w-16 px-2 py-2 text-sm text-center bg-background border border-black/10 dark:border-white/10 focus:ring-2 focus:ring-primary focus:outline-none"
                />
                <input
                  data-testid={`input-${key}-month`} type="number" min="1" max="12" placeholder="MM"
                  value={num(form[`${key}_month`])} onChange={(e) => set(`${key}_month`, e.target.value)}
                  className="w-16 px-2 py-2 text-sm text-center bg-background border border-black/10 dark:border-white/10 focus:ring-2 focus:ring-primary focus:outline-none"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">{t("prof_privacy")}</p>

      <div className="mt-8 border border-primary/25 bg-primary/5 p-5" data-testid="profile-consent">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">
          <ShieldCheck size={14} weight="fill" /> {t("prof_consent_title")}
        </div>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground" data-testid="profile-consent-text">{t("consent")}</p>
      </div>

      <button
        data-testid="save-profile-button" onClick={save} disabled={saving}
        className="mt-8 inline-flex items-center gap-2 bg-primary text-white px-6 py-3 text-sm uppercase tracking-[0.15em] hover:bg-primary/90 transition-colors active:scale-95 disabled:opacity-60"
      >
        {saving ? <CircleNotch size={16} className="animate-spin" weight="bold" /> : <FloppyDisk size={16} weight="bold" />}
        {t("prof_save")}
      </button>
    </div>
  );
}
